/** Política de senha da aplicação (alinhada a app.auth.password_policy). */
import { resolveAppEnvironmentCode } from "./appEnvironment";

/** Piso DEV (lab ``1234``). */
export const PASSWORD_DEV_MIN_LENGTH = 4;
/** IST/UAT e outros não-PRD. */
export const PASSWORD_NONPRD_MIN_LENGTH = 5;
/**
 * Piso de schema / ``minLength`` HTML — o mais baixo entre não-PRD.
 * Preferir ``passwordMinLength()`` para validação e mensagens.
 */
export const PASSWORD_MIN_LENGTH = PASSWORD_DEV_MIN_LENGTH;
export const PASSWORD_PRD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 16;
/** Especiais PRD (A-Z / a-z / 0-9 / estes). */
export const PASSWORD_SPECIAL_CHARS = "!@#$%^&*";

/** Ambiente da app: `prd` marca regras fortes; resto = só comprimento (DEV/UAT). */
export function appPasswordEnvironment() {
  return resolveAppEnvironmentCode() === "prd" ? "prd" : "dev";
}

/** Comprimento mínimo efectivo conforme ``VITE_ENVIRONMENT``. */
export function passwordMinLength() {
  const code = resolveAppEnvironmentCode();
  if (code === "prd") return PASSWORD_PRD_MIN_LENGTH;
  if (code === "dev") return PASSWORD_DEV_MIN_LENGTH;
  return PASSWORD_NONPRD_MIN_LENGTH;
}

export function passwordMinLengthMessage(subject = "A nova senha") {
  return `${subject} deve ter pelo menos ${passwordMinLength()} caracteres.`;
}

export function passwordMaxLengthMessage(subject = "A senha") {
  return `${subject} deve ter no máximo ${PASSWORD_MAX_LENGTH} caracteres.`;
}

/** Texto completo da política PRD (hint + erros). */
export function passwordPrdRequirementsMessage() {
  return (
    `A senha deve ter entre ${PASSWORD_PRD_MIN_LENGTH} e ${PASSWORD_MAX_LENGTH} ` +
    `caracteres e incluir: maiúscula (A-Z), minúscula (a-z), número (0-9) e ` +
    `um carácter especial (${PASSWORD_SPECIAL_CHARS}).`
  );
}

/** Hint do modal conforme ambiente (DEV: só comprimento). */
export function passwordRequirementsMessage() {
  if (resolveAppEnvironmentCode() === "prd") {
    return passwordPrdRequirementsMessage();
  }
  return `A senha deve ter entre ${passwordMinLength()} e ${PASSWORD_MAX_LENGTH} caracteres.`;
}

/**
 * Valida complexidade da nova senha (regras alinhadas ao backend).
 * @returns {string|null} mensagem de erro ou null se válida
 */
export function passwordComplexityError(password) {
  if (typeof password !== "string") {
    return passwordRequirementsMessage();
  }
  if (resolveAppEnvironmentCode() !== "prd") {
    const mn = passwordMinLength();
    if (password.length < mn || password.length > PASSWORD_MAX_LENGTH) {
      return passwordRequirementsMessage();
    }
    return null;
  }
  if (
    password.length < PASSWORD_PRD_MIN_LENGTH ||
    password.length > PASSWORD_MAX_LENGTH
  ) {
    return passwordPrdRequirementsMessage();
  }
  if (!/[A-Z]/.test(password)) {
    return passwordPrdRequirementsMessage();
  }
  if (!/[a-z]/.test(password)) {
    return passwordPrdRequirementsMessage();
  }
  if (!/\d/.test(password)) {
    return passwordPrdRequirementsMessage();
  }
  const specialRe = new RegExp(
    `[${PASSWORD_SPECIAL_CHARS.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}]`
  );
  if (!specialRe.test(password)) {
    return passwordPrdRequirementsMessage();
  }
  const allowedRe = new RegExp(
    `^[A-Za-z\\d${PASSWORD_SPECIAL_CHARS.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}]+$`
  );
  if (!allowedRe.test(password)) {
    return passwordPrdRequirementsMessage();
  }
  return null;
}
